// B2B landing page targeting workshops, ad agencies, property managers,
// public institutions & corporate clients with recurring logistics needs.
// Fully translated (DE/EN/AR) via i18n.
import { Link } from "react-router-dom";
import {
  Briefcase, FileText, Code2, Clock3, Phone, ArrowRight, CheckCircle2,
  Wrench, Building2, ShieldCheck, Megaphone, GraduationCap, Hotel,
  TerminalSquare, Webhook, KeyRound, BookOpen,
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import { BUSINESS_PROFILE } from "../data/seo";
import { useLang } from "../contexts/LangContext";

export default function B2BPage() {
  const { t } = useLang();
  const b = t.b2b || {};

  const SEGMENTS = [
    { icon: Wrench, label: b.seg_werkstatt_label, text: b.seg_werkstatt_text, cases: [b.seg_werkstatt_case1, b.seg_werkstatt_case2, b.seg_werkstatt_case3] },
    { icon: Megaphone, label: b.seg_werbung_label, text: b.seg_werbung_text, cases: [b.seg_werbung_case1, b.seg_werbung_case2, b.seg_werbung_case3] },
    { icon: Building2, label: b.seg_hv_label, text: b.seg_hv_text, cases: [b.seg_hv_case1, b.seg_hv_case2, b.seg_hv_case3] },
    { icon: GraduationCap, label: b.seg_schule_label, text: b.seg_schule_text, cases: [b.seg_schule_case1, b.seg_schule_case2, b.seg_schule_case3] },
    { icon: Hotel, label: b.seg_hotel_label, text: b.seg_hotel_text, cases: [b.seg_hotel_case1, b.seg_hotel_case2, b.seg_hotel_case3] },
    { icon: Briefcase, label: b.seg_shop_label, text: b.seg_shop_text, cases: [b.seg_shop_case1, b.seg_shop_case2, b.seg_shop_case3] },
  ];

  const FEATURES = [
    { icon: FileText, title: b.f_invoice_title, text: b.f_invoice_text },
    { icon: Code2, title: b.f_api_title, text: b.f_api_text },
    { icon: Clock3, title: b.f_sla_title, text: b.f_sla_text },
    { icon: Briefcase, title: b.f_account_title, text: b.f_account_text },
    { icon: ShieldCheck, title: b.f_gdpr_title, text: b.f_gdpr_text },
    { icon: Building2, title: b.f_scale_title, text: b.f_scale_text },
  ];

  const API_STEPS = [
    { icon: BookOpen, title: b.step1_title, text: b.step1_text },
    { icon: KeyRound, title: b.step2_title, text: b.step2_text },
    { icon: TerminalSquare, title: b.step3_title, text: b.step3_text },
    { icon: Webhook, title: b.step4_title, text: b.step4_text },
  ];

  return (
    <div data-testid="b2b-page">
      <SEOHead title={b.meta_title} description={b.meta_desc} />

      {/* ===== HERO ===== */}
      <section className="relative bg-[#090A0B] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: "url(/generated/service_autoteile.png)" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#090A0B] via-[#090A0B]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 text-[#FF5A00] mb-4">
            <Briefcase size={16} />
            <span className="text-xs font-bold tracking-[0.3em] uppercase">{b.hero_eyebrow}</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 leading-[0.95] max-w-4xl">
            {b.hero_title_line1}<br />{b.hero_title_line2}
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed">
            {b.hero_subtitle}
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={`tel:${BUSINESS_PROFILE.telephone.replace(/\s/g, "")}`}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="b2b-call-btn"
            >
              <Phone size={16} /> {b.hero_cta_call}
            </a>
            <a
              href="#api"
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="b2b-api-anchor"
            >
              {b.hero_cta_api} <Code2 size={16} />
            </a>
            <Link
              to="/book"
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
            >
              {b.hero_cta_trial} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEGMENTS ===== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">{b.segments_eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-12 max-w-3xl">
            {b.segments_title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SEGMENTS.map((s, i) => (
              <div
                key={i}
                className="border border-neutral-200 p-7 hover:border-[#FF5A00] hover:shadow-lg transition group"
                data-testid={`b2b-segment-${i}`}
              >
                <s.icon size={28} className="text-[#FF5A00] mb-4 group-hover:scale-110 transition" />
                <div className="text-xl font-black tracking-tight mb-2">{s.label}</div>
                <p className="text-sm text-neutral-600 leading-relaxed mb-4">{s.text}</p>
                <ul className="space-y-1.5 pt-4 border-t border-neutral-100">
                  {s.cases.map((c) => (
                    <li key={c} className="flex items-center gap-2 text-xs text-neutral-700 font-medium">
                      <CheckCircle2 size={13} className="text-[#FF5A00] flex-shrink-0" /> {c}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA] border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">{b.features_eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-12 max-w-3xl">
            {b.features_title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-neutral-200 p-7" data-testid={`b2b-feature-${f.title}`}>
                <f.icon size={22} className="text-[#FF5A00] mb-3" />
                <div className="font-black tracking-tight text-lg mb-2">{f.title}</div>
                <p className="text-sm text-neutral-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== API / INTEGRATION ===== */}
      <section id="api" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-14">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-50 text-[#FF5A00] px-3 py-1.5 mb-4">
                <Code2 size={14} />
                <span className="text-[10px] font-black tracking-[0.25em] uppercase">{b.api_badge}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-6">
                {b.api_title}
              </h2>
              <p className="text-base sm:text-lg text-neutral-700 leading-relaxed mb-6">
                {b.api_intro_pre}<strong>{b.api_intro_strong}</strong>{b.api_intro_post}
              </p>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#FF5A00] mt-0.5 flex-shrink-0" /> <span><strong>{b.api_b1_b}</strong>{b.api_b1}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#FF5A00] mt-0.5 flex-shrink-0" /> <span><strong>{b.api_b2_b}</strong>{b.api_b2}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#FF5A00] mt-0.5 flex-shrink-0" /> <span><strong>{b.api_b3_b}</strong>{b.api_b3}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#FF5A00] mt-0.5 flex-shrink-0" /> <span><strong>{b.api_b4_b}</strong>{b.api_b4}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#FF5A00] mt-0.5 flex-shrink-0" /> <span><strong>{b.api_b5_b}</strong>{b.api_b5}</span></li>
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="/api/docs"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#090A0B] hover:bg-black text-white font-bold uppercase tracking-wide px-6 py-3.5 transition inline-flex items-center gap-2"
                  data-testid="b2b-docs-link"
                >
                  <BookOpen size={16} /> {b.api_open_docs}
                </a>
                <a
                  href={`mailto:${BUSINESS_PROFILE.email}?subject=API%20Partner-Key%20Anfrage&body=Hallo%20Talkani-Team%2C%0A%0Awir%20m%C3%B6chten%20Talkani%20%C3%BCber%20die%20API%20in%20unser%20System%20integrieren.%0A%0AUnser%20Unternehmen%3A%20%0AKontaktperson%3A%20%0AGesch%C3%A4tztes%20Auftragsvolumen%2FMonat%3A%20`}
                  className="border-2 border-[#FF5A00] text-[#FF5A00] hover:bg-[#FF5A00] hover:text-white font-bold uppercase tracking-wide px-6 py-3.5 transition inline-flex items-center gap-2"
                  data-testid="b2b-api-key-cta"
                >
                  <KeyRound size={16} /> {b.api_request_key}
                </a>
              </div>
            </div>

            {/* Code preview — code stays in English/cURL (universal dev language), only the comment is translated */}
            <div dir="ltr" className="bg-[#0A0B0D] text-neutral-200 font-mono text-xs sm:text-sm overflow-x-auto border border-neutral-800 rounded-sm">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-800 bg-[#16181C]">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <span className="ms-3 text-[10px] tracking-widest text-neutral-500 uppercase">POST /api/bookings/guest</span>
              </div>
              <pre className="p-5 leading-relaxed whitespace-pre overflow-x-auto"><code>
{`curl -X POST https://talkani.de/api/bookings/guest \\
  -H "Content-Type: application/json" \\
  -d '{
    "service": "autoteile",
    "date": "2026-03-15",
    "time": "10:30",
    "address": "Werkstatt Müller, Hamburg",
    "phone": "+49 40 1234567",
    "name": "Auto Müller GmbH",
    "email": "dispo@mueller-auto.de",
    "details": "Bremsbeläge VW Golf VII",
    "booking_method": "api"
  }'

`}{b.api_code_response}{`
{
  "booking_id": "bk_a4f2c8e91",
  "status": "pending",
  "service": "autoteile",
  "created_at": "2026-03-15T08:15:32Z"
}`}
              </code></pre>
            </div>
          </div>

          {/* 4 steps */}
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3 mt-6">{b.steps_eyebrow}</p>
          <h3 className="text-2xl sm:text-3xl font-black tracking-tight mb-8">
            {b.steps_title}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {API_STEPS.map((s) => (
              <div key={s.title} className="border border-neutral-200 p-6" data-testid={`b2b-api-step-${s.title}`}>
                <s.icon size={22} className="text-[#FF5A00] mb-3" />
                <div className="font-black tracking-tight mb-2">{s.title}</div>
                <p className="text-xs text-neutral-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA] border-y border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3 text-center">{b.pricing_eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-10 text-center">{b.pricing_title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <PricingTier label={b.tier_starter} jobs={b.tier_starter_jobs} rabatt={b.tier_starter_rabatt} t={b} />
            <PricingTier label={b.tier_growth} jobs={b.tier_growth_jobs} rabatt={b.tier_growth_rabatt} highlight t={b} />
            <PricingTier label={b.tier_enterprise} jobs={b.tier_enterprise_jobs} rabatt={b.tier_enterprise_rabatt} t={b} />
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 bg-[#090A0B] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-6">{b.final_cta_title}</h2>
          <p className="text-neutral-300 mb-8">{b.final_cta_text}</p>
          <a
            href={`mailto:${BUSINESS_PROFILE.email}?subject=Talkani%20Business%20Anfrage`}
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm"
            data-testid="b2b-cta-mail"
          >
            {BUSINESS_PROFILE.email} <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}

const PricingTier = ({ label, jobs, rabatt, highlight = false, t }) => (
  <div className={`p-7 border-2 ${highlight ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200 bg-white"}`} data-testid={`b2b-tier-${label}`}>
    <div className="text-xs font-bold tracking-widest uppercase text-neutral-500 mb-1">{jobs}</div>
    <div className="text-2xl font-black tracking-tight mb-5">{label}</div>
    <div className="text-4xl font-black text-[#FF5A00] tabular-nums mb-2">{rabatt}</div>
    <div className="text-xs text-neutral-500 tracking-wider uppercase">{t.tier_discount_label}</div>
    <ul className="mt-5 space-y-1.5 text-sm text-neutral-700">
      <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5A00]" /> {t.tier_f1}</li>
      <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5A00]" /> {t.tier_f2}</li>
      <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5A00]" /> {t.tier_f3}</li>
      <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5A00]" /> {t.tier_f4}</li>
    </ul>
  </div>
);
